import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { OverlayChartDataPoint, ChartDataPoint } from '../utils/dailyDataProcessor';
import { getXAxisTicks, formatSleepStage, formatPosture, calculateYAxisDomain, calculateTemperatureYAxisDomain } from '../utils/dailyChartUtils';

type DailyOverlayChartProps = {
  overlayData: OverlayChartDataPoint[];
  sleepStageData: ChartDataPoint[];
  noiseData: ChartDataPoint[];
  temperatureData: ChartDataPoint[];
  selectedMetrics: {
    sleepStage: boolean;
    noise: boolean;
    posture: boolean;
    temperature: boolean;
  };
};

export const DailyOverlayChart: React.FC<DailyOverlayChartProps> = ({
  overlayData,
  sleepStageData,
  noiseData,
  temperatureData,
  selectedMetrics,
}) => {
  const OverlayTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-300 rounded-md shadow-lg">
          <p className="font-medium mb-2">{`時刻: ${dataPoint.time}`}</p>
          {payload.map((entry: any, index: number) => {
            let label = '';
            let value = '';
            
            if (entry.dataKey === 'sleepStage') {
              label = '睡眠ステージ';
              value = formatSleepStage(entry.value);
            } else if (entry.dataKey === 'noise') {
              label = 'ノイズレベル';
              value = `${entry.value}dB`;
            } else if (entry.dataKey === 'posture') {
              label = '姿勢';
              value = formatPosture(entry.value);
            } else if (entry.dataKey === 'temperature') {
              label = 'ベッド温度';
              value = `${entry.value}℃`;
            }
            
            return (
              <p key={index} style={{ color: entry.color }}>
                {`${label}: ${value}`}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="chart-container">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">重ね合わせグラフ</h3>
      <div style={{ width: '100%', height: 400 }}>
        <ResponsiveContainer>
          <LineChart data={overlayData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis 
              dataKey="time"
              ticks={getXAxisTicks(sleepStageData)}
              axisLine={{ stroke: '#d1d5db' }}
              tickLine={{ stroke: '#d1d5db' }}
              tick={{ fill: '#374151', fontSize: 12 }}
            />
            {selectedMetrics.sleepStage && (
              <YAxis 
                yAxisId="sleepStage"
                domain={[-0.3, 3.3]}
                ticks={[0, 1, 2, 3]}
                axisLine={{ stroke: '#3b82f6' }}
                tickLine={{ stroke: '#3b82f6' }}
                tick={{ fill: '#3b82f6', fontSize: 12 }}
                tickFormatter={formatSleepStage}
                label={{ value: '睡眠ステージ', angle: -90, position: 'insideLeft', style: { fill: '#3b82f6' } }}
              />
            )}
            {selectedMetrics.noise && (
              <YAxis 
                yAxisId="noise"
                orientation="right"
                domain={calculateYAxisDomain(noiseData.map(d => d.value as number), 15)}
                axisLine={{ stroke: '#ef4444' }}
                tickLine={{ stroke: '#ef4444' }}
                tick={{ fill: '#ef4444', fontSize: 12 }}
                label={{ value: 'ノイズ(dB)', angle: 90, position: 'insideRight', style: { fill: '#ef4444' } }}
              />
            )}
            {selectedMetrics.posture && (
              <YAxis 
                yAxisId="posture"
                orientation="right"
                domain={[-0.3, 2.3]}
                ticks={[2, 1, 0]}
                axisLine={{ stroke: '#10b981' }}
                tickLine={{ stroke: '#10b981' }}
                tick={{ fill: '#10b981', fontSize: 12 }}
                tickFormatter={formatPosture}
                reversed={true}
                label={{ value: '姿勢', angle: 90, position: 'insideRight', style: { fill: '#10b981' } }}
              />
            )}
            {selectedMetrics.temperature && (
              <YAxis 
                yAxisId="temperature"
                orientation="right"
                domain={calculateTemperatureYAxisDomain(temperatureData.map(d => d.value as number), 2)}
                axisLine={{ stroke: '#f59e0b' }}
                tickLine={{ stroke: '#f59e0b' }}
                tick={{ fill: '#f59e0b', fontSize: 12 }}
                label={{ value: '温度(℃)', angle: 90, position: 'insideRight', style: { fill: '#f59e0b' } }}
              />
            )}
            <Tooltip content={<OverlayTooltip />} />
            {selectedMetrics.sleepStage && (
              <Line
                yAxisId="sleepStage"
                type="stepAfter"
                dataKey="sleepStage"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={false}
                name="睡眠ステージ"
              />
            )}
            {selectedMetrics.noise && (
              <Line
                yAxisId="noise"
                type="monotone"
                dataKey="noise"
                stroke="#ef4444"
                strokeWidth={2}
                dot={false}
                name="ノイズレベル"
              />
            )}
            {selectedMetrics.posture && (
              <Line
                yAxisId="posture"
                type="stepAfter"
                dataKey="posture"
                stroke="#10b981"
                strokeWidth={2}
                dot={false}
                name="姿勢"
              />
            )}
            {selectedMetrics.temperature && (
              <Line
                yAxisId="temperature"
                type="monotone"
                dataKey="temperature"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={false}
                name="ベッド温度"
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
